import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, FlagPennant, X } from "@phosphor-icons/react";
import { useRealtime } from "../lib/realtime";

/* In-app top-bar notification toast.
   Listens to SSE messages and shows the latest as a top sliding card for ~3 s.
   Click → navigate to the notification link. */
export default function NotificationToast() {
  const { subscribe } = useRealtime();
  const [stack, setStack] = useState([]); // {id, title, body, link, urgent}
  const navigate = useNavigate();

  useEffect(() => {
    return subscribe((msg) => {
      if (msg.type !== "notification") return;
      const n = msg.data;
      setStack((s) => [...s, n]);
      setTimeout(() => setStack((s) => s.filter((x) => x.id !== n.id)), 3000);
    });
  }, [subscribe]);

  function dismiss(id) { setStack((s) => s.filter((x) => x.id !== id)); }
  function open(n) { dismiss(n.id); if (n.link) navigate(n.link); }

  return (
    <div className="pointer-events-none fixed inset-x-0 top-0 z-[100] flex flex-col items-center gap-2 pt-safe">
      <AnimatePresence>
        {stack.slice(-3).map((n) => (
          <motion.button key={n.id} data-testid={`toast-${n.id}`}
            initial={{ opacity: 0, y: -24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -16, scale: 0.98 }}
            transition={{ duration: 0.22, ease: [0.2, 0.7, 0.2, 1] }}
            onClick={() => open(n)}
            className={`pointer-events-auto mt-2 flex w-[min(28rem,calc(100%-1rem))] items-start gap-3 rounded-2xl border bg-ink-900/95 px-3 py-2.5 text-left backdrop-blur-xl shadow-[0_20px_60px_rgba(0,0,0,0.6)] ${n.urgent ? "border-neon-pink/40" : "border-white/10"}`}>
            <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ${n.urgent ? "bg-neon-pink/15 text-neon-pink" : "bg-neon-cyan/15 text-neon-cyan"}`}>
              {n.urgent ? <FlagPennant size={16} weight="fill" /> : <Bell size={16} weight="fill" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className={`truncate text-[13px] font-bold ${n.urgent ? "text-neon-pink" : "text-white"}`}>{n.title}</p>
              {n.body && <p className="line-clamp-2 text-[12px] text-white/65">{n.body}</p>}
            </div>
            <span onClick={(e) => { e.stopPropagation(); dismiss(n.id); }} className="rounded-full bg-white/5 p-1 text-white/55"><X size={12} weight="bold" /></span>
          </motion.button>
        ))}
      </AnimatePresence>
    </div>
  );
}
